import { Link, useLocation } from "react-router-dom";
import {
  // UPPDRAG-30: Globe icon dropped — Domäner moved into Inställningar
  // as a sub-tab so the top-level admin nav has fewer items.
  LayoutDashboard, Grid3X3, UserSearch, Columns3, DollarSign, CalendarDays, Clock, Repeat, Users, Trophy, GraduationCap, Crown, Megaphone, BarChart3, Settings, ArrowLeftRight, FileText, LogOut
} from "lucide-react";
import { cn } from "@/lib/utils";
import { isNavActive } from "@/lib/navActive";
import SportIcon from "@/components/SportIcon";
import { useOrganization } from "@/hooks/useOrganization";
import { useAppMode } from "@/hooks/useAppMode";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const groups = [
  {
    labelKey: "sidebarGroups.overview",
    items: [
      { to: "/manager/dashboard", icon: LayoutDashboard, labelKey: "manager.dashboard" },
      { to: "/manager/schedule", icon: Grid3X3, labelKey: "manager.schedule", permission: "schedule" },
    ],
  },
  {
    labelKey: "sidebarGroups.customersAndCourts",
    items: [
      { to: "/manager/customers", icon: UserSearch, labelKey: "manager.customers", permission: "customers" },
      { to: "/manager/courts", icon: Columns3, labelKey: "manager.courts", permission: "courts" },
      { to: "/manager/pricing", icon: DollarSign, labelKey: "manager.pricing", permission: "pricing" },
    ],
  },
  {
    labelKey: "sidebarGroups.bookings",
    items: [
      { to: "/manager/bookings", icon: CalendarDays, labelKey: "manager.bookings", permission: "bookings" },
      { to: "/manager/waitlist", icon: Clock, labelKey: "manager.waitlist", feature: "waitlist", permission: "bookings" },
      { to: "/manager/recurring", icon: Repeat, labelKey: "manager.recurring", feature: "recurring", permission: "bookings" },
    ],
  },
  {
    labelKey: "sidebarGroups.activities",
    items: [
      { to: "/manager/leagues", icon: Trophy, labelKey: "manager.leagues", feature: "leagues", permission: "leagues" },
      // UPPDRAG-22: Lag entry removed — moved to a tab inside AdminLeagues
      // (was a standalone sidebar entry in PR-12.3). See AdminLeagues
      // viewMode="teams" + the Lag button in the toolbar.
      { to: "/manager/tournaments", icon: ({ className }: { className?: string }) => <SportIcon sport="padel" size="sm" className={className} />, labelKey: "manager.tournaments", feature: "tournaments", permission: "tournaments" },
      { to: "/manager/training", icon: GraduationCap, labelKey: "manager.training", feature: "training", permission: "training" },
    ],
  },
  {
    labelKey: "sidebarGroups.administration",
    items: [
      { to: "/manager/users", icon: Users, labelKey: "manager.users", permission: "users" },
      { to: "/manager/membership", icon: Crown, labelKey: "manager.membership", feature: "memberships", permission: "memberships" },
      { to: "/manager/invoices", icon: FileText, labelKey: "manager.invoicing", feature: "invoicing", permission: "invoicing" },
      { to: "/manager/broadcast", icon: Megaphone, labelKey: "manager.broadcast", feature: "community", permission: "community" },
      { to: "/manager/reports", icon: BarChart3, labelKey: "manager.reports", permission: "reports" },
      // UPPDRAG-30: Domäner moved to a tab inside Inställningar (alongside
      // Webhooks & API). Old /manager/domains URL still works — it
      // redirects to /manager/settings?tab=domains via App.tsx.
      { to: "/manager/settings", icon: Settings, labelKey: "manager.settings", permission: "settings" },
    ],
  },
];

export default function ManagerSidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const location = useLocation();
  const { organization } = useOrganization();
  const { setMode } = useAppMode();
  const { isEnabled } = useFeatureFlags();
  const { can } = usePermissions();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  const switchToPlayer = () => {
    onNavigate?.();
    setMode("player");
    navigate("/");
  };

  const handleLogout = async () => {
    onNavigate?.();
    try { await signOut(); } catch { /* signOut already logs errors */ }
    // Hard redirect to /auth — matches Profile.tsx pattern and ensures any
    // stale UI state is cleared.
    window.location.href = "/auth";
  };

  return (
    <aside className="sticky top-0 flex h-dvh w-60 flex-col border-r bg-card">
      <Link
        to="/manager/dashboard"
        className="flex flex-col items-center justify-center border-b px-5 gradient-sidebar hover:opacity-90 transition-opacity"
      >
        {(() => {
          const sidebarDisplay = (organization?.settings as any)?.sidebar_display ??
            (organization?.logo_url ? "logo" : "name");
          if (sidebarDisplay === "logo" && organization?.logo_url) {
            return (
              <div className="flex flex-col items-center gap-1 py-4">
                <img src={organization.logo_url} alt={organization.name ?? ""} className="h-10 max-w-[160px] object-contain" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Manager</span>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2 h-16 self-start">
              {organization?.logo_url && (
                <img src={organization.logo_url} alt="" className="h-8 w-8 rounded-md object-cover" />
              )}
              <div className="min-w-0">
                <span className="block truncate text-sm font-semibold">{organization?.name ?? "Klubben"}</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Manager</span>
              </div>
            </div>
          );
        })()}
      </Link>

      {/* V2 Issue 4.6: komprimerad spacing för att rymma alla 17 menyval
       *  utan inre scroll vid vanligt desktop-viewport (1920×853). Tidigare
       *  overflow ≈ 148px på grund av py-2 + space-y-4. */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {groups.map((group) => {
          const visibleItems = group.items.filter(
            (item: any) => (!item.feature || isEnabled(item.feature)) && (!item.permission || can(item.permission))
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.labelKey}>
              <p className="px-3 mb-1 text-[10px] font-semibold tracking-widest text-muted-foreground/60 uppercase">
                {t(group.labelKey)}
              </p>
              <div className="space-y-0.5">
                {visibleItems.map(({ to, icon: Icon, labelKey }) => {
                  // G-097: boundary prefix match so drilldowns (/manager/customers/:id)
                  // keep their section highlighted.
                  const active = isNavActive(location.pathname, to);
                  return (
                    <Link
                      key={to}
                      to={to}
                      onClick={onNavigate}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors",
                        active
                          ? "bg-primary/10 text-primary font-medium border-l-[3px] border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground border-l-[3px] border-transparent"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{t(labelKey)}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t p-3 space-y-0.5">
        <button
          onClick={switchToPlayer}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <ArrowLeftRight className="h-4 w-4" />
          <span>{t("nav.switchToPlayer")}</span>
        </button>
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <LogOut className="h-4 w-4" />
          <span>{t("logout")}</span>
        </button>
      </div>
    </aside>
  );
}
